<script setup lang="ts">
import { ref, computed } from 'vue';
import type { ExpandableSection } from '@/data/help';

const props = defineProps<{
  section: ExpandableSection;
}>();

const isExpanded = ref(props.section.defaultExpanded ?? false);

function toggle() {
  isExpanded.value = !isExpanded.value;
}

// Simple markdown parsing for content
function parseContent(content: string): string {
  if (!content) return '';

  let html = content.trim();

  // Escape HTML
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_match, _lang, code) => {
    return `<pre class="code-block"><code>${code.trim()}</code></pre>`;
  });

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Tables
  html = html.replace(/\|(.+)\|\n\|[-| ]+\|\n((?:\|.+\|\n?)+)/g, (_, header, body) => {
    const headerCells = header.split('|').map((c: string) => c.trim()).filter(Boolean);
    const headerRow = headerCells.map((c: string) => `<th>${c}</th>`).join('');

    const bodyRows = body.trim().split('\n').map((row: string) => {
      const cells = row.split('|').map((c: string) => c.trim()).filter(Boolean);
      return `<tr>${cells.map((c: string) => `<td>${c}</td>`).join('')}</tr>`;
    }).join('');

    return `<table class="help-table"><thead><tr>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table>`;
  });

  // Bold and italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Lists
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Horizontal rules
  html = html.replace(/^---$/gm, '<hr>');

  // Paragraphs
  html = html
    .split('\n\n')
    .map(para => {
      para = para.trim();
      if (!para) return '';
      if (para.startsWith('<h') || para.startsWith('<ul') || para.startsWith('<ol') ||
          para.startsWith('<pre') || para.startsWith('<table') || para.startsWith('<hr')) {
        return para;
      }
      return `<p>${para.replace(/\n/g, '<br>')}</p>`;
    })
    .join('\n');

  return html;
}

const parsedContent = computed(() => parseContent(props.section.content));
</script>

<template>
  <div class="expandable-section" :class="{ expanded: isExpanded }">
    <button class="expandable-header" @click="toggle">
      <i class="bi" :class="isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
      <span class="expandable-title">{{ section.title }}</span>
    </button>
    <Transition name="expand">
      <div v-if="isExpanded" class="expandable-content">
        <div class="expandable-body" v-html="parsedContent"></div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.expandable-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
}

.expandable-section.expanded {
  border-color: #d1d5db;
}

.expandable-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: #f9fafb;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s;
}

.expandable-header:hover {
  background: #f3f4f6;
}

.expandable-header i {
  font-size: 12px;
  color: #6b7280;
  transition: transform 0.15s;
}

.expandable-title {
  flex: 1;
}

.expandable-content {
  overflow: hidden;
}

.expandable-body {
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  background: #ffffff;
}

.expandable-body :deep(p) {
  margin: 0 0 12px 0;
}

.expandable-body :deep(p:last-child) {
  margin-bottom: 0;
}

.expandable-body :deep(ul),
.expandable-body :deep(ol) {
  margin: 0 0 12px 0;
  padding-left: 24px;
}

.expandable-body :deep(li) {
  margin: 4px 0;
}

.expandable-body :deep(code) {
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 13px;
  color: #dc2626;
}

.expandable-body :deep(pre.code-block) {
  background: #1f2937;
  color: #e5e7eb;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 12px 0;
}

.expandable-body :deep(pre.code-block code) {
  background: none;
  padding: 0;
  color: inherit;
}

.expandable-body :deep(.help-table) {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 13px;
}

.expandable-body :deep(.help-table th),
.expandable-body :deep(.help-table td) {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.expandable-body :deep(.help-table th) {
  background: #f9fafb;
  font-weight: 600;
}

.expandable-body :deep(strong) {
  font-weight: 600;
  color: #1f2937;
}

.expandable-body :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 16px 0;
}

/* Expand transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 1000px;
}
</style>

<!-- Dark mode styles (unscoped) -->
<style>
[data-bs-theme="dark"] .expandable-section {
  border-color: #334155;
}

[data-bs-theme="dark"] .expandable-section.expanded {
  border-color: #475569;
}

[data-bs-theme="dark"] .expandable-header {
  background: #1e293b;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .expandable-header:hover {
  background: #334155;
}

[data-bs-theme="dark"] .expandable-header i {
  color: #94a3b8;
}

[data-bs-theme="dark"] .expandable-body {
  background: #0f172a;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .expandable-body code {
  background: #334155;
  color: #fca5a5;
}

[data-bs-theme="dark"] .expandable-body pre.code-block {
  background: #1e293b;
}

[data-bs-theme="dark"] .expandable-body .help-table th,
[data-bs-theme="dark"] .expandable-body .help-table td {
  border-color: #334155;
}

[data-bs-theme="dark"] .expandable-body .help-table th {
  background: #1e293b;
}

[data-bs-theme="dark"] .expandable-body strong {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .expandable-body hr {
  border-top-color: #334155;
}
</style>
